import { ref, uploadBytesResumable, uploadBytes, getDownloadURL, getBytes, deleteObject } from "firebase/storage";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { storage, db } from "../firebase";
import { v4 as uuidv4 } from "uuid";

const DB_NAME = "flowframe-scores";
const DB_VERSION = 1;
const STORE_NAME = "pdfs";

function openCacheDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const idb = request.result;
      if (!idb.objectStoreNames.contains(STORE_NAME)) {
        idb.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function cachePDF(scoreId, arrayBuffer) {
  try {
    const idb = await openCacheDB();
    const tx = idb.transaction(STORE_NAME, "readwrite");
    // Store a fresh copy so callers don't need to worry about detached buffers
    tx.objectStore(STORE_NAME).put(arrayBuffer.slice(0), scoreId);
    await new Promise((res, rej) => {
      tx.oncomplete = res;
      tx.onerror = rej;
    });
  } catch (e) {
    console.warn("Failed to cache PDF locally:", e);
  }
}

export async function getCachedPDF(scoreId) {
  try {
    const idb = await openCacheDB();
    const tx = idb.transaction(STORE_NAME, "readonly");
    const request = tx.objectStore(STORE_NAME).get(scoreId);
    return new Promise((resolve) => {
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function removeCachedPDF(scoreId) {
  try {
    const idb = await openCacheDB();
    const tx = idb.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(scoreId);
  } catch {
    /* ignore */
  }
}

// Firebase Storage helpers

export function uploadScore(userId, scoreId, file, onProgress) {
  const storagePath = `users/${userId}/scores/${scoreId}.pdf`;
  const storageRef = ref(storage, storagePath);
  const uploadTask = uploadBytesResumable(storageRef, file);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress?.(pct);
      },
      reject,
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        resolve({ storagePath, downloadURL });
      }
    );
  });
}

export async function getScoreDownloadURL(storagePath) {
  return getDownloadURL(ref(storage, storagePath));
}

export async function deleteScoreFile(storagePath) {
  await deleteObject(ref(storage, storagePath));
}

export async function uploadThumbnail(userId, scoreId, blob) {
  const storagePath = `users/${userId}/scores/${scoreId}_thumb.jpg`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, blob, { contentType: "image/jpeg" });
  return getDownloadURL(storageRef);
}

// Firestore CRUD for score metadata

function scoresCol(userId) {
  return collection(db, "users", userId, "scores");
}

function scoreDoc(userId, scoreId) {
  return doc(db, "users", userId, "scores", scoreId);
}

export function generateScoreId() {
  return uuidv4();
}

export async function saveScoreMetadata(userId, scoreData) {
  const docRef = scoreDoc(userId, scoreData.id);
  await setDoc(docRef, {
    ...scoreData,
    uploadedAt: scoreData.uploadedAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function getScoreMetadata(userId, scoreId) {
  const snap = await getDoc(scoreDoc(userId, scoreId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getAllScores(userId) {
  const q = query(scoresCol(userId), orderBy("uploadedAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function deleteScoreMetadata(userId, scoreId) {
  await deleteDoc(scoreDoc(userId, scoreId));
}

export async function updateScoreField(userId, scoreId, fields) {
  await updateDoc(scoreDoc(userId, scoreId), {
    ...fields,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Fetches the PDF as an ArrayBuffer, using the IndexedDB cache first.
 * Returns a Blob URL suitable for react-pdf's <Document file={...}>.
 * Uses Firebase SDK's getBytes() to avoid CORS issues with fetch().
 */
export async function loadScorePDF(userId, scoreId, storagePath) {
  let buffer = await getCachedPDF(scoreId);

  if (!buffer) {
    const storageRef = ref(storage, storagePath);
    buffer = await getBytes(storageRef);
    await cachePDF(scoreId, buffer.slice(0));
  }

  return URL.createObjectURL(new Blob([buffer], { type: "application/pdf" }));
}

// ─── Setlist CRUD ────────────────────────────────────────────────────────────

function setlistsCol(userId) {
  return collection(db, "users", userId, "setlists");
}

function setlistDoc(userId, setlistId) {
  return doc(db, "users", userId, "setlists", setlistId);
}

export function generateSetlistId() {
  return uuidv4();
}

export async function saveSetlist(userId, setlistData) {
  const docRef = setlistDoc(userId, setlistData.id);
  await setDoc(docRef, {
    ...setlistData,
    createdAt: setlistData.createdAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function getAllSetlists(userId) {
  const q = query(setlistsCol(userId), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function deleteSetlist(userId, setlistId) {
  await deleteDoc(setlistDoc(userId, setlistId));
}

export async function updateSetlist(userId, setlistId, fields) {
  await updateDoc(setlistDoc(userId, setlistId), {
    ...fields,
    updatedAt: serverTimestamp(),
  });
}

// ─── Performance recording uploads ───────────────────────────────────────────

function sanitizeFilename(name) {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 120);
}

export function uploadPerformanceRecording(userId, performanceId, recordingId, file, onProgress) {
  const safeName = sanitizeFilename(file.name || `recording-${recordingId}`);
  const storagePath = `users/${userId}/performances/${performanceId}/recordings/${recordingId}_${safeName}`;
  const storageRef = ref(storage, storagePath);
  const uploadTask = uploadBytesResumable(storageRef, file, { contentType: file.type || "audio/mpeg" });

  return new Promise((resolve, reject) => {
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress?.(pct);
      },
      reject,
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        resolve({ storagePath, downloadURL });
      }
    );
  });
}

export async function deletePerformanceRecording(storagePath) {
  if (!storagePath) return;
  try {
    await deleteObject(ref(storage, storagePath));
  } catch (err) {
    if (err?.code !== "storage/object-not-found") throw err;
  }
}
